import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';

import { Button, Grid, makeStyles } from '@material-ui/core';
import DefaultTable from '../components/DefaultTable';
import PageWrapper from '../components/PageWrapper';
import * as Popover from '../components/PopoverMenu';

import { contractsListColumns } from '../table-configs/columns-schemas';

import { fetchLine } from 'app/store/slices/line';
import { URLS } from 'app/config/urls';
import moment from 'moment';
import InfoLineCompany from '../components/InfoLineCompany';
import lineEndpoint from '../../../api/services/lineEndpoint';
import ListsFilters from '../components/ListsFilters';
import SaveBox from '../components/SaveBox';
import { useTableReducer } from '../hooks/useTableReducer';
import { usePersistentFilters } from '../hooks/usePersistentFilters';

const ContractList = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();

    const [contracts, actions] = useTableReducer();
    const { changePage, changePageSize, resetTable, loadData: loadContracts } = actions;

    const cdLinhaFromPath = React.useRef(match.params.cdLinha);
    const selectedLine = useSelector((state) => state.line.selected);
    const isStrip = useSelector((state) => state.line.isStrip);

    const [loadingRows, setLoadingRows] = useState(true);
    const [editEl, setEditEl] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [orderBy, setOrderBy] = useState('contratoTermoAditivo.cdContratoTermoAditivo');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [canAddNewRevision, setCanAddNewRevision] = useState(false);

    const initialFilters = useMemo(() => {
        const result = {
            campoOrdenacao: orderBy,
            direcaoOrdenacao: orderDirection,
        };
        if (cdLinhaFromPath && cdLinhaFromPath.current) {
            result.cdLinha = Number(cdLinhaFromPath.current);
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderBy, orderDirection, cdLinhaFromPath]);

    const [filters, setFilters] = usePersistentFilters(initialFilters, 'contracts-filters');

    const [rows, setRows] = useState([]);
    const open = Boolean(editEl);

    const handleChangeOrder = (event, property) => {
        let tempDirection = orderDirection;
        if (property === orderBy) {
            tempDirection = orderDirection === 'desc' ? 'asc' : 'desc';
        }
        const temp = { ...filters };
        temp['campoOrdenacao'] = `contratoTermoAditivo.${property}`;
        if (property === 'deTipoContrato') {
            temp['campoOrdenacao'] = `tipoContrato.${property}`;
        }
        temp['direcaoOrdenacao'] = tempDirection;
        setOrderDirection(tempDirection);
        setOrderBy(property);
        setFilters(temp);
        handleChangePageSize(null, 0);
    };

    useEffect(() => {
        (async () => {
            if (cdLinhaFromPath && cdLinhaFromPath.current) {
                await dispatch(fetchLine(cdLinhaFromPath.current));
            }
        })();
    }, [dispatch]);

    useEffect(() => {
        setLoadingRows(true);
        (async () => {
            const params = {
                page: filters.page ? filters.page : contracts.page,
                size: filters.pageSize ? filters.pageSize : contracts.pageSize,
                request: filters
            };
            const response = await lineEndpoint.getContracts(params);
            if (response && response.status === 200) {
                const resData = response.data;
                if (resData.content.length === 0) {
                    resetTable();
                } else {
                    loadContracts(resData);
                }
            }
            setLoadingRows(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contracts.page, contracts.pageSize, filters]);

    useEffect(() => {
        (() => {
            if (selectedLine) {
                setCanAddNewRevision(true);
            }
        })();
    }, [selectedLine]);

    const prepareRows = (data) => {
        if (!data) return [];
        return data.map((row) => {
            const temp = { ...row };
            temp.dtInicioVigencia = temp.dtInicioVigencia ? moment(temp.dtInicioVigencia).format('DD/MM/YYYY') : '';
            temp.dtVencimento = temp.dtVencimento ? moment(temp.dtVencimento).format('DD/MM/YYYY') : '';
            temp.deTipoContrato = temp.tipoContrato?.deTipoContrato;
            return temp;
        });
    };

    useEffect(() => setRows(prepareRows(contracts.content)), [contracts]);

    const handleOpenEditMenu = (event, data) => {
        setEditEl(event.currentTarget);
        setSelectedEntry(data);
    };

    const handleCloseEditMenu = () => {
        setSelectedEntry(null);
        setEditEl(null);
    };

    const handleChangePage = (e, page) => {
        changePage(page);
        const temp = { ...filters };
        temp['page'] = page;
        setFilters(temp);
        console.log({ page });
    };

    const handleChangePageSize = (e) => {
        let pageSize = parseInt(e.target.value);
        changePageSize(pageSize);
        const temp = { ...filters };
        temp['pageSize'] = pageSize;
        setFilters(temp);
    };

    const handleNewButton = async () => {
        let params = { cdLinha: match.params.cdLinha };
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE_CONTRACT_NEW, params));
    };

    const handleGoBack = () => {
        let params = { cdLinha: match.params.cdLinha };
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE, params));
    };

    return (
        <>
            <PageWrapper
                title="Lista de Contratos, Termos de Permissão e Aditivos de Contrato"
                actionButton={
                    canAddNewRevision ? (
                        <Button
                            variant="contained"
                            color="primary"
                            disableElevation
                            className={classes.addButton}
                            onClick={handleNewButton}>
                            Novo
                        </Button>
                    ) : (
                        <></>
                    )
                }>
                <Grid container spacing={4}>
                    <Grid item container spacing={3} direction={'row'}>
                        <InfoLineCompany selectedLine={selectedLine} />
                    </Grid>

                    <ListsFilters filters={filters} setFilters={setFilters} hasSituation={false} />

                    <Grid item xs={12}>
                        <DefaultTable
                            columns={contractsListColumns}
                            rows={rows}
                            loading={loadingRows}
                            onMore={handleOpenEditMenu}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: contracts.count,
                                rowsPerPage: filters.pageSize ? filters.pageSize : contracts.pageSize,
                                page: filters.page ? filters.page : contracts.page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}
                            orderBy={orderBy}
                            order={orderDirection}
                            onRequestSort={handleChangeOrder}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            firstButtonLabel="Voltar"
                            onCancel={handleGoBack}
                            middleButtonLabel={null}
                            lastButtonLabel={null}
                            removeSpacing
                        />
                    </Grid>
                </Grid>
            </PageWrapper>
            <Popover.ContractList
                onEdit={handleOpenEditMenu}
                open={open}
                anchorEl={editEl}
                handleClose={handleCloseEditMenu}
                dataPath={`${URLS.LINES_MANAGE}/${match.params.cdLinha}/contracts`}
                dataId={selectedEntry?.cdContratoTermoAditivo}
                data={selectedEntry}
                isActive={selectedEntry?.boAtivo}
                isStrip={isStrip}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    addButton: {
        borderRadius: '25px',
        color: theme.palette.primary.contrastText,
    },
}));

export default ContractList;
